import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LocalStorageService } from './localStorage.service';

export interface StripeLocationPayload {
  address: string;
  city: string;
  zip_code: string;
  country: string; // Code pays sur 2 caractères (ex: FR, US, etc.)
}

export interface StripeLocationValidationResult {
  isValid: boolean;
  city: string;
  country: string;
  countryCode: string;
  errors?: {
    address?: string;
    city?: string;
    zip_code?: string;
    country?: string;
    country_code?: string;
  };
  message?: string;
  suggestion?: string;
  errorCode?: string;
  error?: string;
}

export interface GeocodingResponse {
  results: Array<{
    address_components: Array<{
      long_name: string;
      short_name: string;
      types: string[];
    }>;
    formatted_address: string;
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
      location_type?: string;
    };
    partial_match?: boolean;
    types?: string[];
  }>;
  status: string;
}

export interface PlacesResponse {
  results: Array<{
    name: string;
    types: string[];
    formatted_address: string;
  }>;
  status: string;
}

export interface AddressValidationResult {
  isValid: boolean;
  city: string;
  country: string;
  countryCode: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AddressValidationService {
  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService
  ) {}

  /**
   * Validate location information with Stripe verification API
   */
  verifyStripeLocation(payload: StripeLocationPayload): Observable<StripeLocationValidationResult> {
    const normalizedPayload = {
      address: this.normalizeTextForStripe(payload.address),
      city: this.normalizeTextForStripe(payload.city),
      zip_code: this.normalizePostalCodeForStripe(payload.zip_code),
      country: (payload.country || '').trim()
    };

    if (!normalizedPayload.address || !normalizedPayload.city || !normalizedPayload.zip_code || !normalizedPayload.country) {
      return of({
        isValid: false,
        city: normalizedPayload.city || '',
        country: normalizedPayload.country || '',
        countryCode: normalizedPayload.country || '',
        errors: {
          address: !normalizedPayload.address ? 'Adresse requise' : undefined,
          city: !normalizedPayload.city ? 'Ville requise' : undefined,
          zip_code: !normalizedPayload.zip_code ? 'Code postal requis' : undefined,
          country: !normalizedPayload.country ? 'Pays requis' : undefined
        },
        message: 'Données incomplètes pour la vérification Stripe'
      });
    }

    // S'assurer que le code pays est sur 2 caractères
    const countryCode = normalizedPayload.country.length === 2 
      ? normalizedPayload.country.toUpperCase() 
      : normalizedPayload.country.substring(0, 2).toUpperCase();

    const apiPayload = {
      address: normalizedPayload.address,
      city: normalizedPayload.city,
      zip_code: normalizedPayload.zip_code,
      country: countryCode
    };

    const url = `${environment.apiUrl}/frontoffice/stripe/verify-location`;
    return this.http.post<any>(url, apiPayload).pipe(
      map((response: any) => {
        // Déterminer si la validation est valide
        const isValid = response?.acceptable ?? 
                       response?.stripe_validation?.valid ?? 
                       false;

        // Extraire les détails de l'adresse depuis response.details ou utiliser les valeurs par défaut
        const details = response?.details || {};
        const city = details.city ?? response?.city ?? normalizedPayload.city;
        const country = details.country ?? response?.country ?? countryCode;

        // Extraire le code d'erreur et les informations supplémentaires
        const stripeValidation = response?.stripe_validation || {};
        const errorCode = stripeValidation.error;
        const suggestion = response?.suggestion;

        // Construire les erreurs selon le code d'erreur et les validations individuelles
        const errors = this.buildErrorsFromResponse(response, stripeValidation);

        // Utiliser le message de stripe_validation ou le message général
        const message = stripeValidation.message ?? 
                       response?.message ?? 
                       (isValid ? 'Localisation valide' : 'Localisation invalide');

        return {
          isValid,
          city,
          country,
          countryCode: country,
          errors: errors && Object.keys(errors).length > 0 ? errors : undefined,
          message,
          suggestion,
          errorCode
        } as StripeLocationValidationResult;
      }),
      catchError((error) => {
        const apiErrors = error?.error?.errors;
        return of({
          isValid: false,
          city: normalizedPayload.city,
          country: countryCode,
          countryCode: countryCode,
          errors: apiErrors,
          message: error?.error?.message || 'Erreur lors de la vérification Stripe'
        } as StripeLocationValidationResult);
      })
    );
  }

  private normalizeTextForStripe(value: string): string {
    if (!value) return '';
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[’`]/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }

  private normalizePostalCodeForStripe(value: string): string {
    if (!value) return '';
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '')
      .trim();
  }

  /**
   * Geocode an address using Google Maps API
   */
  geocodeAddress(address: string): Observable<GeocodingResponse> {
    const settings = this.localStorageService.getSettings();
    const apiKey = settings?.google_maps_key || environment.map_key;
    
    if (!apiKey) {
      return of({
        status: 'ERROR',
        results: []
      } as GeocodingResponse);
    }

    const geocodeUrl = 'https://maps.googleapis.com/maps/api/geocode/json';
    const params = new HttpParams()
      .set('address', address)
      .set('key', apiKey)
      .set('language', 'fr');

    return this.http.get<GeocodingResponse>(geocodeUrl, { params });
  }

  /**
   * Validate city using Google Maps Geocoding API
   */
  validateCity(city: string, countryCode: string): Observable<AddressValidationResult> {
    if (!city || !countryCode) {
      return of({
        isValid: false,
        city: city || '',
        country: '',
        countryCode: countryCode || '',
        error: 'La ville et le code pays sont requis'
      });
    }

    const searchAddress = `${city}, ${countryCode}`;
    
    return this.geocodeAddress(searchAddress).pipe(
      map(response => {
        if (response.status !== 'OK' || !response.results.length) {
          return {
            isValid: false,
            city: city,
            country: '',
            countryCode: countryCode,
            error: 'Ville non trouvée'
          };
        }

        const result = response.results[0];
        const addressComponents = result.address_components;
        
        const foundCity = addressComponents.find(component => 
          component.types.includes('locality') || 
          component.types.includes('administrative_area_level_2') ||
          component.types.includes('postal_town')
        );
        
        const foundCountry = addressComponents.find(component => 
          component.types.includes('country')
        );

        if (!foundCity || !foundCountry) {
          return {
            isValid: false,
            city: city,
            country: '',
            countryCode: countryCode,
            error: 'Ville non trouvée dans le pays spécifié'
          };
        }

        const countryMatches = foundCountry.short_name.toUpperCase() === countryCode.toUpperCase();
        
        if (!countryMatches) {
          return {
            isValid: false,
            city: city,
            country: '',
            countryCode: countryCode,
            error: 'Ville non trouvée dans le pays spécifié'
          };
        }

        return {
          isValid: true,
          city: foundCity.long_name,
          country: foundCountry.long_name,
          countryCode: foundCountry.short_name,
          coordinates: {
            lat: result.geometry.location.lat,
            lng: result.geometry.location.lng
          }
        };
      }),
      catchError(error => of({
        isValid: false,
        city: city,
        country: '',
        countryCode: countryCode,
        error: 'Erreur lors de la validation de la ville'
      }))
    );
  }

  /**
   * Validate address using Google Maps Geocoding API
   */
  validateAddress(address: string, city: string, countryCode: string): Observable<AddressValidationResult> {
    if (!address || !city || !countryCode) {
      return of({
        isValid: false,
        city: city || '',
        country: '',
        countryCode: countryCode || '',
        error: 'L\'adresse, la ville et le code pays sont requis'
      });
    }

    const searchAddress = `${address}, ${city}, ${countryCode}`;
    
    return this.geocodeAddress(searchAddress).pipe(
      map(response => {
        if (response.status !== 'OK' || !response.results.length) {
          return {
            isValid: false,
            city: city,
            country: '',
            countryCode: countryCode,
            error: 'Adresse non trouvée'
          };
        }

        const result = response.results[0];
        const addressComponents = result.address_components;
        
        const foundCity = addressComponents.find(component => 
          component.types.includes('locality') || 
          component.types.includes('administrative_area_level_2') ||
          component.types.includes('postal_town')
        );
        
        const foundCountry = addressComponents.find(component => 
          component.types.includes('country')
        );

        if (!foundCity || !foundCountry) {
          return {
            isValid: false,
            city: city,
            country: '',
            countryCode: countryCode,
            error: 'Adresse non trouvée dans la ville spécifiée'
          };
        }

        const countryMatches = foundCountry.short_name.toUpperCase() === countryCode.toUpperCase();
        const cityMatches = foundCity.long_name.toLowerCase().includes(city.toLowerCase()) ||
                           city.toLowerCase().includes(foundCity.long_name.toLowerCase());
        
        if (!countryMatches || !cityMatches) {
          return {
            isValid: false,
            city: city,
            country: '',
            countryCode: countryCode,
            error: 'Adresse non trouvée dans la ville spécifiée'
          };
        }

        // Check if the result is too generic (only city/country without specific address)
        const hasSpecificAddress = addressComponents.some(component => 
          component.types.includes('street_number') || 
          component.types.includes('route') ||
          component.types.includes('premise')
        );

        if (!hasSpecificAddress) {
          return {
            isValid: false,
            city: foundCity.long_name,
            country: foundCountry.long_name,
            countryCode: foundCountry.short_name,
            error: 'Adresse trop générique, veuillez fournir une adresse plus spécifique'
          };
        }

        return {
          isValid: true,
          city: foundCity.long_name,
          country: foundCountry.long_name,
          countryCode: foundCountry.short_name,
          coordinates: {
            lat: result.geometry.location.lat,
            lng: result.geometry.location.lng
          }
        };
      }),
      catchError(error => of({
        isValid: false,
        city: city,
        country: '',
        countryCode: countryCode,
        error: 'Erreur lors de la validation de l\'adresse'
      }))
    );
  }

  /**
   * Validate that a postal code corresponds to the specified city
   */
  validatePostalCodeCityConsistency(
    postalCode: string, 
    city: string, 
    countryCode: string
  ): Observable<AddressValidationResult> {
    if (!postalCode || !countryCode) {
      return of({
        isValid: false,
        city: city || '',
        country: '',
        countryCode: countryCode || '',
        error: 'Le code postal et le code pays sont requis'
      });
    }

    // Nettoyer les valeurs
    const cleanPostalCode = postalCode.trim();
    const cleanCity = city?.trim() || '';
    
    // Rechercher avec le code postal seul pour obtenir la ville réelle associée
    const searchAddressByPostalCode = `${cleanPostalCode}, ${countryCode}`;
    
    return this.geocodeAddress(searchAddressByPostalCode).pipe(
      map(response => this.processPostalCodeCityValidation(response, cleanPostalCode, cleanCity, countryCode)),
      catchError(error => of({
        isValid: false,
        city: cleanCity,
        country: '',
        countryCode: countryCode,
        error: 'Erreur lors de la validation de la cohérence code postal/ville'
      }))
    );
  }

  /**
   * Process postal code and city consistency validation response
   */
  private processPostalCodeCityValidation(
    response: GeocodingResponse, 
    postalCode: string, 
    city: string, 
    countryCode: string
  ): AddressValidationResult {
    if (response.status !== 'OK' || !response.results.length) {
      return {
        isValid: false,
        city: city,
        country: '',
        countryCode: countryCode,
        error: 'Le code postal saisi est invalide ou n\'existe pas'
      };
    }

    // Vérifier si le résultat est seulement le pays (code postal invalide)
    const firstResult = response.results[0];
    const hasOnlyCountry = firstResult.address_components.every(component => 
      component.types.includes('country') || component.types.includes('political')
    );
    
    const isPartialMatch = firstResult.partial_match === true;
    const hasOnlyCountryType = firstResult.types && 
                               firstResult.types.length === 2 && 
                               firstResult.types.includes('country') && 
                               firstResult.types.includes('political');

    // Si le résultat est seulement le pays, le code postal est invalide
    if (hasOnlyCountry || (isPartialMatch && hasOnlyCountryType)) {
      return {
        isValid: false,
        city: city,
        country: '',
        countryCode: countryCode,
        error: 'Le code postal saisi est invalide ou n\'existe pas'
      };
    }

    // Parcourir tous les résultats pour trouver une correspondance
    let foundValidPostalCode = false;
    
    for (const result of response.results) {
      const addressComponents = result.address_components;
      
      const foundPostalCode = addressComponents.find(component => 
        component.types.includes('postal_code')
      );
      
      const foundCity = addressComponents.find(component => 
        component.types.includes('locality') || 
        component.types.includes('administrative_area_level_2') ||
        component.types.includes('postal_town') ||
        component.types.includes('sublocality') ||
        component.types.includes('sublocality_level_1')
      );
      
      const foundCountry = addressComponents.find(component => 
        component.types.includes('country')
      );

      if (!foundPostalCode) {
        continue;
      }

      foundValidPostalCode = true;

      // Vérifier que le code postal correspond EXACTEMENT
      const postalCodeMatches = foundPostalCode.long_name === postalCode || 
                                foundPostalCode.short_name === postalCode;
      
      if (!postalCodeMatches || !foundCountry) {
        continue;
      }

      const countryMatches = foundCountry.short_name.toUpperCase() === countryCode.toUpperCase();
      
      if (!countryMatches) {
        continue;
      }

      // Si la ville n'est pas fournie, accepter le code postal s'il existe pour le pays
      if (!city || city.trim() === '') {
        return {
          isValid: true,
          city: foundCity?.long_name || '',
          country: foundCountry.long_name,
          countryCode: foundCountry.short_name,
          coordinates: {
            lat: result.geometry.location.lat,
            lng: result.geometry.location.lng
          }
        };
      }

      // Si on a une ville dans les résultats, vérifier la correspondance
      if (foundCity) {
        const normalizeCityName = (name: string): string => {
          return name
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        };

        const normalizedInputCity = normalizeCityName(city);
        const normalizedFoundCity = normalizeCityName(foundCity.long_name);
        const normalizedFoundCityShort = normalizeCityName(foundCity.short_name);

        const cityMatches = normalizedFoundCity === normalizedInputCity ||
                           normalizedFoundCityShort === normalizedInputCity ||
                           normalizedFoundCity.includes(normalizedInputCity) ||
                           normalizedInputCity.includes(normalizedFoundCity);

        if (cityMatches) {
          return {
            isValid: true,
            city: foundCity.long_name,
            country: foundCountry.long_name,
            countryCode: foundCountry.short_name,
            coordinates: {
              lat: result.geometry.location.lat,
              lng: result.geometry.location.lng
            }
          };
        }
      }
    }

    // Si aucun code postal valide n'a été trouvé
    if (!foundValidPostalCode) {
      return {
        isValid: false,
        city: city,
        country: '',
        countryCode: countryCode,
        error: 'Le code postal saisi est invalide ou n\'existe pas'
      };
    }

    return {
      isValid: false,
      city: city,
      country: '',
      countryCode: countryCode,
      error: 'Le code postal ne correspond pas à la ville indiquée'
    };
  }

  /**
   * Get a list of major cities for a given country using Google Places API
   */
  getCitiesForCountry(countryCode: string): Observable<string[]> {
    if (!countryCode) {
      return of([]);
    }

    // Get Google Maps API key from settings or environment
    const settings = this.localStorageService.getSettings();
    const apiKey = settings?.google_maps_key || environment.map_key;
    
    if (!apiKey) {
      return of([]);
    }

    // Use Google Places API to get cities for the country
    const placesApiUrl = 'https://maps.googleapis.com/maps/api/place/textsearch/json';
    const params = new HttpParams()
      .set('query', `cities in ${countryCode}`)
      .set('key', apiKey)
      .set('language', 'fr')
      .set('region', countryCode.toLowerCase());

    return this.http.get<PlacesResponse>(placesApiUrl, { params }).pipe(
      map(response => {
        if (response.status === 'OK' && response.results) {
          const cities = response.results
            .filter((place: { types: string[] }) => 
              place.types.includes('locality') || 
              place.types.includes('administrative_area_level_2')
            )
            .map((place: { name: string }) => place.name)
            .filter((city: string, index: number, array: string[]) => array.indexOf(city) === index) // Remove duplicates
            .slice(0, 50); // Limit to 50 cities for performance

          return cities;
        }
        
        return [];
      }),
      catchError((error) => {
        console.warn('Erreur lors de la récupération des villes:', error);
        return of([]);
      })
    );
  }

  /**
   * Construit les erreurs à partir de la réponse de l'API
   */
  private buildErrorsFromResponse(
    response: any, 
    stripeValidation: any
  ): StripeLocationValidationResult['errors'] {
    const errors: StripeLocationValidationResult['errors'] = {};
    const errorCode = stripeValidation.error;
    const message = stripeValidation.message || response?.message || '';

    // Mapper les erreurs selon le code d'erreur
    switch (errorCode) {
      case 'INVALID_ADDRESS':
      case 'ADDRESS_NOT_FOUND':
      case 'ADDRESS_MISMATCH':
      case 'ADDRESS_TOO_SHORT':
      case 'INVALID_ADDRESS_FORMAT':
        errors.address = message;
        if (stripeValidation.found_address) {
          errors.address += ` Adresse suggérée: ${stripeValidation.found_address}`;
        }
        break;

      case 'INVALID_CITY':
      case 'CITY_NOT_FOUND':
      case 'CITY_MISMATCH':
        errors.city = message;
        if (stripeValidation.found_city) {
          errors.city += ` Ville suggérée: ${stripeValidation.found_city}`;
        }
        break;

      case 'INVALID_ZIP_CODE_FORMAT':
      case 'ZIP_CODE_NOT_FOUND':
      case 'ZIP_CODE_MISMATCH':
      case 'ZIP_CODE_CITY_MISMATCH':
        errors.zip_code = message;
        if (stripeValidation.found_postal_code) {
          errors.zip_code += ` Code postal suggéré: ${stripeValidation.found_postal_code}`;
        }
        break;

      case 'CITY_ZIP_MISMATCH':
        // Créer des messages spécifiques pour chaque champ invalide
        // Le message de l'API est générique et parle des deux, donc on le personnalise
        if (response?.city_valid === false) {
          errors.city = 'La ville fournie ne correspond pas au code postal.';
        }
        if (response?.zip_code_valid === false) {
          errors.zip_code = 'Le code postal ne correspond pas à la ville fournie.';
        }
        break;

      case 'COUNTRY_MISMATCH':
        errors.country = message;
        if (stripeValidation.found_country) {
          errors.country += ` Pays trouvé: ${stripeValidation.found_country}`;
        }
        break;

      case 'DATA_INCONSISTENCY':
        // Pour les incohérences, utiliser des messages spécifiques par champ
        if (response?.city_valid === false) {
          errors.city = message || 'La ville n\'est pas valide.';
        }
        if (response?.zip_code_valid === false) {
          errors.zip_code = message || 'Le code postal n\'est pas valide.';
        }
        if (response?.address_valid === false) {
          errors.address = message || 'L\'adresse n\'est pas valide.';
        }
        break;

      case 'ZERO_RESULTS':
        // Messages spécifiques pour chaque champ
        errors.address = message || 'Aucune adresse trouvée.';
        errors.city = message || 'Aucune ville trouvée.';
        errors.zip_code = message || 'Aucun code postal trouvé.';
        break;

      default:
        // Si pas de code d'erreur spécifique, utiliser les validations individuelles
        // Créer des messages spécifiques pour chaque champ invalide
        if (response?.address_valid === false) {
          errors.address = message || 'L\'adresse n\'est pas valide selon Stripe.';
        }
        if (response?.city_valid === false) {
          errors.city = message || 'La ville n\'est pas valide selon Stripe.';
        }
        if (response?.zip_code_valid === false) {
          errors.zip_code = message || 'Le code postal n\'est pas valide selon Stripe.';
        }
        if (response?.country_supported === false) {
          errors.country = message || 'Le pays n\'est pas supporté par Stripe.';
        }
        break;
    }

    return errors;
  }
}
